import { motion } from 'framer-motion';

export const Problem = () => {
  const apps = [
    { name: 'Calendar', icon: '📅', color: 'bg-blue-100 dark:bg-blue-900/30' },
    { name: 'Notes', icon: '📝', color: 'bg-amber-100 dark:bg-amber-900/30' },
    { name: 'Reminders', icon: '⏰', color: 'bg-red-100 dark:bg-red-900/30' },
    { name: 'Tasks', icon: '✅', color: 'bg-green-100 dark:bg-green-900/30' },
    { name: 'Email', icon: '📧', color: 'bg-purple-100 dark:bg-purple-900/30' },
    { name: 'Slack', icon: '💬', color: 'bg-pink-100 dark:bg-pink-900/30' },
    { name: 'Drive', icon: '📁', color: 'bg-indigo-100 dark:bg-indigo-900/30' },
    { name: 'Docs & scans', icon: '📄', color: 'bg-cyan-100 dark:bg-cyan-900/30' },
  ];

  return (
    <section className="py-24 lg:py-32 bg-slate-50/50 dark:bg-slate-900/50 overflow-hidden" data-testid="problem-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left - Visual */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="relative">
              {/* Scattered Apps */}
              <div className="grid grid-cols-3 gap-4 max-w-sm mx-auto">
                {apps.map((app, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.8, rotate: Math.random() * 10 - 5 }}
                    whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    className={`${app.color} rounded-2xl p-4 flex flex-col items-center justify-center aspect-square shadow-lg`}
                  >
                    <span className="text-3xl mb-1">{app.icon}</span>
                    <span className="text-xs font-medium text-slate-600 dark:text-slate-300">{app.name}</span>
                  </motion.div>
                ))}
              </div>

              {/* Chaos Lines */}
              <div className="absolute inset-0 -z-10">
                <svg className="w-full h-full opacity-20" viewBox="0 0 400 400">
                  <path d="M50,50 Q200,100 350,50" stroke="currentColor" fill="none" strokeWidth="2" className="text-red-400" />
                  <path d="M50,150 Q200,200 350,150" stroke="currentColor" fill="none" strokeWidth="2" className="text-orange-400" />
                  <path d="M50,250 Q200,300 350,250" stroke="currentColor" fill="none" strokeWidth="2" className="text-yellow-400" />
                </svg>
              </div>
            </div>
          </motion.div>

          {/* Right - Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <span className="text-red-500 font-semibold text-sm uppercase tracking-wider">The Problem</span>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 dark:text-white leading-tight">
              You already have 8 apps
              <br />
              <span className="text-slate-500 dark:text-slate-400">open right now, don't you?</span>
            </h2>
            <p className="mt-6 text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
              Calendar. Notes. Reminders. Tasks. Email. Chat. Files — plus somewhere to stash tickets and PDFs,
              then dig through them for a PNR or date.
              <span className="block mt-2">
                Switching between apps all day isn't productivity — it's exhaustion.
                Every switch costs you 23 minutes of focus time.
              </span>
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <div className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-4 py-2 rounded-full text-sm font-medium">
                23 min lost per context switch
              </div>
              <div className="bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 px-4 py-2 rounded-full text-sm font-medium">
                2+ hours wasted daily
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
